import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Defensive guard against stuck body scroll locks left behind by
 * Radix UI Dialog / Sheet / DropdownMenu / Popover when they unmount
 * mid-animation (common on mobile after route changes or fast taps).
 *
 * Symptoms it fixes:
 *  - Page can't scroll on mobile until refresh
 *  - body has lingering `overflow: hidden`, `pointer-events: none`,
 *    or `data-scroll-locked` attributes
 */
function clearBodyLocks() {
  const body = document.body;
  if (!body) return;

  // Radix sets these inline styles to lock scroll
  if (body.style.overflow === "hidden") body.style.overflow = "";
  if (body.style.pointerEvents === "none") body.style.pointerEvents = "";
  if (body.style.position === "fixed") {
    body.style.position = "";
    body.style.top = "";
    body.style.left = "";
    body.style.right = "";
    body.style.width = "";
  }
  // Radix marker attribute
  if (body.hasAttribute("data-scroll-locked")) {
    body.removeAttribute("data-scroll-locked");
  }

  // Also clear on <html> just in case
  const html = document.documentElement;
  if (html.style.overflow === "hidden") html.style.overflow = "";
}

export function ScrollLockReset() {
  const location = useLocation();

  // Reset on every route change
  useEffect(() => {
    // Run on next tick so any unmounting overlay finishes cleanup first
    const t = setTimeout(clearBodyLocks, 50);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Periodic safety net: if body ends up locked while no Radix overlay
  // is actually open, unlock it. Cheap check, only inspects body.
  useEffect(() => {
    const interval = setInterval(() => {
      const body = document.body;
      if (!body) return;
      const hasOpenOverlay = !!document.querySelector(
        '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], [data-radix-popper-content-wrapper]'
      );
      const isLocked =
        body.style.overflow === "hidden" ||
        body.style.pointerEvents === "none" ||
        body.hasAttribute("data-scroll-locked");
      if (isLocked && !hasOpenOverlay) {
        clearBodyLocks();
      }
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  return null;
}
